import React from 'react';
import { Link } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import { PageTitle } from '@/components/common/PageTitle';

const STEPS: { title: string; desc: string; badge: string }[] = [
  {
    badge: '1',
    title: '카페24 앱스토어에서 설치 · 계정 연동',
    desc: '카페24 앱스토어에서 "alt 자동 생성"을 설치하면 alt.aigeneration-tech.com 계정 로그인(또는 회원가입) 후 카페24 몰과 자동으로 연동됩니다.',
  },
  {
    badge: '2',
    title: '평소처럼 상품을 등록·수정',
    desc: '카페24 관리자 화면에서 평소 하던 대로 상품을 등록하거나 수정하면 됩니다 - 별도로 확장 프로그램을 켜거나 버튼을 누를 필요가 없습니다.',
  },
  {
    badge: '3',
    title: 'ALT 텍스트가 자동으로 채워집니다',
    desc: '상품 대표이미지와 상세설명 안의 이미지 중 ALT가 비어 있는 것만 AI가 자동으로 채웁니다. 이미 사람이 직접 입력해둔 ALT는 덮어쓰지 않습니다.',
  },
];

const FAQS: { q: string; a: React.ReactNode }[] = [
  {
    q: '설치 후 바로 사용할 수 있나요?',
    a: '네. 앱 설치 및 계정 연동이 끝나면 별도 설정 없이 바로 동작합니다.',
  },
  {
    q: '신규 등록과 수정, 처리 속도가 다른가요?',
    a: '신규 상품 등록은 즉시(수 초 이내) 처리됩니다. 상품 수정은 카페24 쪽 실시간 알림이 아직 연결되지 않아, 최대 10분 주기 재조회를 통해 반영됩니다.',
  },
  {
    q: '어떤 이미지에 ALT가 채워지나요?',
    a: '상품 대표이미지 1개와, 상세설명 안에 들어간 이미지 전부가 대상입니다. 목록/썸네일 이미지는 대상이 아닙니다.',
  },
  {
    q: '이미 ALT를 직접 입력해둔 이미지는 어떻게 되나요?',
    a: '건드리지 않습니다. ALT가 비어 있는 이미지만 골라서 채우고, 값이 있는 이미지는 그대로 보존합니다.',
  },
  {
    q: '연동 이전에 이미 등록해둔 상품은 어떻게 하나요?',
    a: (
      <>
        자동 처리는 연동 이후의 신규/수정 건부터 적용됩니다. 기존에 등록된 상품은{' '}
        <Link to="/dashboard/shop-extension-guide#cafe24" className="underline font-semibold">
          쇼핑몰 직접연동(Cafe24)
        </Link>{' '}
        메뉴에서 쇼핑몰 주소를 입력하면 일괄로 처리할 수 있습니다.
      </>
    ),
  },
  {
    q: '무료로 사용할 수 있나요?',
    a: (
      <>
        네, 무료로 40건 가능합니다.{' '}
        <Link to="/dashboard/my/plan" className="underline font-semibold">
          요금제
        </Link>
        를 확인해 주세요.
      </>
    ),
  },
];

export const Cafe24AppGuide: React.FC = () => (
  <>
    <PageTitle title="카페24 alt 자동 생성 사용 가이드" />
    <PageContainer maxWidth="default">
      <div className="rounded-2xl overflow-hidden shadow-md mb-8 max-w-[480px] mx-auto">
        <img
          src="/apps_explain.png"
          alt="alt 자동 생성 - 상품 이미지의 ALT 텍스트를 AI가 자동으로 작성해드립니다"
          className="w-full h-auto block"
        />
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-3">카페24 "alt 자동 생성" 사용 가이드</h1>
      <p className="text-gray-700 mb-8 text-lg">
        카페24 계정을 한 번 연동해두면, 그 이후 상품을 등록하거나 수정할 때마다 이미지 ALT 텍스트가
        자동으로 채워집니다. 웹 접근성을 개선하고 이미지 검색(SEO) 노출도 함께 늘어납니다.
      </p>

      <section className="mb-10">
        <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">🚀 이용 흐름</h2>
        <div className="space-y-4">
          {STEPS.map((step) => (
            <div key={step.badge} className="flex items-start gap-4 bg-white border border-gray-200 rounded-lg p-6">
              <span className="flex-shrink-0 w-9 h-9 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                {step.badge}
              </span>
              <div>
                <p className="font-semibold text-gray-900 mb-1">{step.title}</p>
                <p className="text-gray-600 text-sm">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10 bg-cyan-50 border-l-4 border-cyan-500 p-5 rounded-r-lg">
        <h3 className="font-bold text-lg mb-3 text-gray-800">⏱️ 처리 속도</h3>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span><strong>신규 상품 등록</strong> - 즉시 처리</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span><strong>기존 상품 수정</strong> - 최대 10분 이내 자동 반영</span>
          </li>
        </ul>
      </section>

      <section className="mb-10 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
        <p className="font-semibold text-yellow-800">💡 이미 등록해둔 상품이 있다면</p>
        <p className="text-yellow-700 text-sm mt-1">
          자동 처리는 연동 이후의 신규/수정 건부터 적용됩니다. 기존 상품은{' '}
          <Link to="/dashboard/shop-extension-guide#cafe24" className="underline font-semibold">
            쇼핑몰 직접연동(Cafe24)
          </Link>{' '}
          메뉴에서 쇼핑몰 주소를 입력하면 한 번에 처리할 수 있습니다.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">❓ 자주 묻는 질문</h2>
        <div className="space-y-3">
          {FAQS.map((item) => (
            <div key={item.q} className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="font-semibold text-gray-900 mb-1">Q. {item.q}</p>
              <p className="text-gray-600 text-sm">A. {item.a}</p>
            </div>
          ))}
        </div>
      </section>
    </PageContainer>
  </>
);

export default Cafe24AppGuide;
